import * as React from "react";
import * as THREE from "three";

import { useFrame, useThree } from "@react-three/fiber";
import { Asset, Hotspot, TourData } from "../../../Types";
import { Html, OrbitControlsProps, Plane } from "@react-three/drei";
import Waypoint from "../Hotspots/Waypoint";
import Popup from "../Hotspots/Popup";
import LinkHotspot from "../Hotspots/Link";
import Quiz from "../Hotspots/Quiz";
import Audio from "../Hotspots/Audio";
import { useCameraPositionStore } from "../../../state management/zustand/gl-stores";
import { useRightSidePanelStore } from "../../../state management/zustand/editor-elements-stores";
import {
  useCurrentSceneStore,
  useEditingElementStore,
} from "../../../api/Zustand";

const dir = new THREE.Vector3();

const spotPos = new THREE.Vector3();

const Spot = (props: {
  hotspots: Hotspot[];
  spot: Hotspot;
  tourData: TourData;
  AudioList: Asset[] | undefined;
  handlePosChange: (pos: THREE.Vector3) => void;
  onDragStart: () => void;
}) => {
  const { spot } = props;

  const { gl } = useThree();

  const rightPanel = useRightSidePanelStore();

  const camera = useCameraPositionStore();

  const { id, setEditingElem } = useEditingElementStore();

  const [dragging, setDragging] = React.useState<boolean>(false);

  const invisiblePlaneRef = React.useRef<THREE.Mesh>(null);
  const wholeSpotRef = React.useRef<THREE.Mesh>(null);

  const position = React.useMemo(() => {
    if (spot.DRAGGING) return wholeSpotRef.current?.position;
    return new THREE.Vector3(spot.x, spot.y, spot.z);
  }, [spot]);

  useFrame((state) => {
    const camera = state.camera as THREE.PerspectiveCamera;

    //WAYPOINT MOVEMENT
    if (props.spot.EDIT_POS && wholeSpotRef.current) {
      const caster = state.raycaster;

      caster.setFromCamera(state.pointer, state.camera);

      const points = caster.intersectObjects(
        state.scene.children.filter((one) => one.name === "sfera")
      );
      const orbicontrols = state.controls as OrbitControlsProps;
      if (dragging) {
        orbicontrols.enabled = false;

        wholeSpotRef.current.position.lerp(points[0].point, 0.9);
      } else orbicontrols.enabled = true;
    }

    camera.getWorldDirection(dir);

    // if (!props.spot.EDIT_POS) state.gl.domElement.style.cursor = "default";

    if (invisiblePlaneRef.current && wholeSpotRef.current) {
      const offsetVal =
        wholeSpotRef.current.position.distanceTo(dir.multiplyScalar(200)) / 150;

      const value = ((3 - offsetVal) * camera.fov * 0.9) / 75;

      invisiblePlaneRef.current.scale.set(value, value, 1);
      invisiblePlaneRef.current.rotation.copy(camera.rotation);
    }
  });

  const { currentSceneID } = useCurrentSceneStore();

  const hsShouldBeSemiOpaque = React.useMemo(() => {
    const currentScene = props.tourData.scenes.find(
      (one) => one.id === currentSceneID
    );

    const hsInEdit = currentScene?.hotspots.find(
      (one) => one.EDIT_POS || one.isNew
    );

    if (hsInEdit) {
      return hsInEdit.id !== props.spot.id;
    } else return false;
  }, [props.tourData, currentSceneID, props.spot]);

  React.useEffect(() => {
    if (spot.EDIT_POS) {
      if (dragging) {
        gl.domElement.style.cursor = "grabbing";
      }
    } else gl.domElement.style.cursor = "auto";
  }, [dragging, spot.EDIT_POS, gl.domElement]);

  return (
    <mesh ref={wholeSpotRef} key={props.spot.id} position={position}>
      {spot.EDIT_POS && (
        <Plane
          visible={false}
          material={new THREE.MeshBasicMaterial({ depthTest: false })}
          onPointerEnter={() => {
            if (spot.EDIT_POS) return (gl.domElement.style.cursor = "grab");
            else gl.domElement.style.cursor = "auto";
          }}
          onPointerLeave={() => (gl.domElement.style.cursor = "auto")}
          //
          onPointerDown={(e) => {
            if (e.button === 0 && spot.EDIT_POS) {
              setDragging(true);

              props.onDragStart();
            } else return;
          }}
          onPointerUp={(e) => {
            if (spot.EDIT_POS) {
              setDragging(false);
              gl.domElement.style.cursor = "grab";

              wholeSpotRef.current?.position.copy(e.point);
              props.handlePosChange(e.point);
            }
          }}
          ref={invisiblePlaneRef}
          args={[15, 15]}
        />
      )}

      <Html
        center
        position={[0, 0, 0]}
        zIndexRange={[0, 2]}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onPointerUp={(e) => {
          //@ts-ignore
          // controls.enabled = true;
        }}
        style={{
          // visibility: "hidden",
          pointerEvents: props.hotspots?.some((spot) => spot.EDIT_POS === true)
            ? "none"
            : "auto",
          opacity: hsShouldBeSemiOpaque ? 0.25 : 1,
          transition: "all 100ms ease-in-out",
        }}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
            rightPanel.setOpen(true);

            setEditingElem(props.spot.type, props.spot.id);

            const position = wholeSpotRef.current?.getWorldPosition(spotPos);

            if (position) {
              camera.setCameraLookAt(position.multiplyScalar(-1));
            }
          }}
          style={{
            userSelect: "none",
            border:
              id === props.spot.id
                ? props.spot.EDIT_POS
                  ? "dashed #ff8700"
                  : "solid #6b7ef9"
                : "none",
            width: "max-content",
          }}
        >
          {/* <b>{JSON.stringify(spot.DRAGGING)}</b> */}
          {props.spot.type === "transition" ? (
            <Waypoint spot={props.spot} tourData={props.tourData} />
          ) : props.spot.type === "popup" ? (
            <div onWheel={(e) => e.stopPropagation()}>
              <Popup spot={props.spot} tourData={props.tourData} />
            </div>
          ) : props.spot.type === "link" ? (
            <LinkHotspot spot={props.spot} />
          ) : props.spot.type === "quiz" ? (
            <Quiz spot={props.spot} />
          ) : props.spot.type === "audio" && props.AudioList ? (
            <Audio spot={props.spot} AudioList={props.AudioList} />
          ) : null}
        </div>
      </Html>
    </mesh>
  );
};

export default Spot;
